import Dexie, { Table } from 'dexie';

export interface IExternalLessonPlan {
  id: number;
  index: number;
  description: string;
  status: string;
  source: string;
  chapterId: number;
  schoolClassId: number;
  cachedAt: number;
}

export interface IExternalLessonPlanDetail {
  id: number;
  index: number;
  source: string;
  description: string;
  content: string;
  gradeSubject: number;
  bookChapter: number;
  cachedAt: number;
}

export class ExternalLessonPlanDB extends Dexie {
  externalLessonPlans!: Table<IExternalLessonPlan>;
  externalLessonPlanDetails!: Table<IExternalLessonPlanDetail>;

  constructor() {
    super('ExternalLessonPlanDB');
    this.version(1).stores({
      externalLessonPlans: 'id, chapterId, schoolClassId, cachedAt',
      externalLessonPlanDetails: 'id, bookChapter, cachedAt',
    });
  }

  async cacheChapterLessonPlans(
    chapterId: number,
    schoolClassId: number,
    lessonPlans: Array<Omit<IExternalLessonPlan, 'chapterId' | 'schoolClassId' | 'cachedAt'>>,
  ): Promise<void> {
    const now = Date.now();
    const records: IExternalLessonPlan[] = lessonPlans.map((lp) => ({
      ...lp,
      chapterId,
      schoolClassId,
      cachedAt: now,
    }));
    await this.externalLessonPlans.bulkPut(records);
  }

  async getChapterLessonPlans(
    chapterId: number,
    schoolClassId: number,
  ): Promise<IExternalLessonPlan[]> {
    return this.externalLessonPlans
      .where('chapterId')
      .equals(chapterId)
      .filter((lp) => lp.schoolClassId === schoolClassId)
      .sortBy('index');
  }

  async cacheLessonPlanDetail(
    detail: Omit<IExternalLessonPlanDetail, 'cachedAt'>,
  ): Promise<void> {
    await this.externalLessonPlanDetails.put({
      ...detail,
      cachedAt: Date.now(),
    });
  }

  async getLessonPlanDetail(
    id: number,
  ): Promise<IExternalLessonPlanDetail | undefined> {
    return this.externalLessonPlanDetails.get(id);
  }

  async updateLessonPlanStatus(
    id: number,
    chapterId: number,
    schoolClassId: number,
    status: string,
  ): Promise<void> {
    await this.externalLessonPlans
      .where('chapterId')
      .equals(chapterId)
      .filter((lp) => lp.id === id && lp.schoolClassId === schoolClassId)
      .modify({ status });
  }
}

export const externalLessonPlanDB = new ExternalLessonPlanDB();
